/**
 * voice.js - everything Pip says in Bubble Pop.
 *
 * CONTENT, NOT MECHANICS. Selection (never the same line twice in a row, and
 * never a repeat until the pool is exhausted) is ../../shared/lineBag.js. Rendering
 * of {placeholders} is renderLine() at the foot of this file.
 *
 * ---------------------------------------------------------------------------
 * MOST OF THE PRAISE IS THE PLATFORM'S
 *
 * Section 8.5 requires 100+ unique praise phrases and forbids repeating one
 * twice in a row, because (section 2.3) repetition and predictable patterns are
 * actively harmful for children on the autism spectrum.
 *
 * The pools live in ../../shared/voice.js. Building a second game showed why:
 * a per-GAME hundred is a rule nobody can keep at a hundred games, and it is
 * the CHILD who must not hear a line twice, and a child plays several games.
 * What stays here is the dozen lines only a game about bubbles can say - "one
 * bubble down" would be nonsense anywhere else.
 *
 * ---------------------------------------------------------------------------
 * FOUR POOLS, FOUR DIFFERENT TRUTHS
 *
 * Praise belongs to a CORRECT production. A child who vocalised without
 * producing the word has done something real - it is Phase 1 component B, the
 * box this game exists to make measurable - and it must be acknowledged, not
 * praised as if it were the target and not corrected as if it were nothing.
 * That is ENCOURAGEMENT. A child who produced a different word gets NEARLY. A
 * child who has run out of tries gets ASSIST, in which Pip does it with them
 * and nobody has failed.
 *
 * ---------------------------------------------------------------------------
 * TONE
 *
 * Every line carries `tone`. Constant brightness is exhausting and, for a child
 * who is already dysregulated, aversive. ../../shared/lineBag.js prefers calm lines
 * when the child is finding a level hard and bright ones when they are flying.
 */

import * as SHARED from '../../shared/voice.js';
import { mergePools } from '../../shared/voice.js';

/**
 * @typedef {{text:string, tone:'bright'|'calm'}} Line
 */

/** Spoken and shown when the child produced the target. */
export const PRAISE = mergePools(SHARED.PRAISE, [
  { text: 'Off it goes.', tone: 'bright' },
  { text: 'Away it floats.', tone: 'calm' },
  { text: 'Pop.', tone: 'bright' },
  { text: 'One bubble down.', tone: 'bright' },
  { text: 'Another one popped.', tone: 'bright' },
  { text: 'Your voice popped it.', tone: 'bright' },
  { text: 'That bubble heard you coming.', tone: 'bright' },
  { text: 'Straight through it.', tone: 'bright' },
  { text: 'Pip felt that one.', tone: 'bright' },
  { text: 'The whole sky heard you.', tone: 'bright' },
  { text: 'Bubbles love that word.', tone: 'bright' },
  { text: 'Pip heard that from across the meadow.', tone: 'bright' },
]);

/**
 * The child made a sound but no word was recognised.
 *
 * This is Phase 1 component B succeeding. It is a real achievement and the
 * only pool that is allowed to answer a trial with no transcript.
 */
export const ENCOURAGEMENT = mergePools(SHARED.ENCOURAGEMENT, [
  { text: 'That sound made the bubbles wobble.', tone: 'bright' },
  { text: 'The bubbles felt that.', tone: 'bright' },
]);

/** A word came back, but not the one the level asked for. Never "wrong". */
export const NEARLY = mergePools(SHARED.NEARLY, [
  { text: 'Not that bubble. Listen to Pip.', tone: 'calm' },
]);

/** Tries are used up. Pip does it with them; nobody has failed. */
export const ASSIST = mergePools(SHARED.ASSIST, [
  { text: 'Pip will pop it, and you can watch.', tone: 'calm' },
]);

/**
 * The prompt, by phase and by support rung.
 *
 * THE RUNG KEYS ARE ENGINE VALUES. They are SUPPORT_LADDER ids from
 * client/src/engine/framework.js, and the game may only use rungs inside the
 * phase's own bounds. A phase's prompt STYLE is the engine's (section 8.2) -
 * these are that style's words, which is the part the engine cannot supply.
 *
 * PROMPTS EVOLVE. Phase 1 is an explicit command that contains the answer -
 * "Say apple" - because Phase 1 is echoic and section 8.2 requires Pip to speak
 * the word aloud. By Phase 2's own rung the prompt is an open question that
 * contains no answer at all - "What is in this one?" - and by Phase 3 it asks
 * the child to decide what should happen, not to repeat anything. A child who
 * is only ever asked to repeat only ever learns to repeat.
 *
 * Placeholders: {word} {phrase} {letter} {clue} {carrier} {carriers}
 */
export const PROMPTS = {
  1: {
    // Section 8.2, phase 1 mustInclude: 'Pip says "Say [WORD]"'. Every line
    // here contains the word, spoken aloud. That is the phase, not a choice.
    full_model: [
      'Say {word}.',
      'Look, a {word}. Say {word}.',
      'Here comes a {word}. Say {word}.',
      'Your turn. Say {word}.',
      'This one is a {word}. Say {word}.',
      'Ready? Say {word}.',
    ],
  },
  2: {
    // No word anywhere. The picture is the only cue.
    visual: [
      'What is in this one?',
      'Tell Pip what you see.',
      'What is that?',
      'Who is inside this bubble?',
      'What have we here?',
      'What shall we call this one?',
    ],
    // The first sound, and nothing more.
    partial_verbal: [
      'It starts with {letter}.',
      '{letter}. What is it?',
      'It begins {letter}. You know this one.',
      'Listen. {letter}. What comes next?',
    ],
    // The answer, given plainly, because the child needs it now.
    full_model: [
      'It is a {word}. Say {word}.',
      'This one is a {word}. Say {word} with Pip.',
      '{word}. Now you say {word}.',
    ],
  },
  3: {
    // Pip points. No words offered at all.
    gestural: [
      'Which one shall we have?',
      'Tell Pip what to do.',
      'What happens next?',
      'It is your turn to decide.',
    ],
    // The word frames are on screen (section 8.2 mustInclude for phase 3).
    visual: [
      'Two words, and Pip will do it.',
      'Tell Pip in two words.',
      'Use one word from each side.',
      'What do you want Pip to do?',
    ],
    // The carrier, given; the child supplies the noun.
    partial_verbal: [
      '{carrier}, and then which one?',
      'Start with {carrier}.',
      '{carrier}. Now the name.',
    ],
    full_model: [
      'Say {phrase}.',
      'Like this. {phrase}. Now you.',
      'Pip would say {phrase}. Your turn.',
    ],
  },
};

/** Pip's semantic clue. Fires as `hintFired` on the trial record. */
export const HINTS = {
  1: ['{clue}', 'Here is a clue. {clue}'],
  2: ['{clue}', 'A clue for you. {clue}', 'Think about this. {clue}'],
  3: [
    'You can say {carriers}, and then the name.',
    'Two words. One from each side.',
    'Pick a word on the left, then the name of the thing.',
  ],
};

/** Spoken once when a level opens. Sets the scene; never explains rules twice. */
export const LEVEL_INTRO = {
  1: [
    'Bubbles are coming. Say what you see, and they pop.',
    'Here come the bubbles. Pip will say the word first.',
    'Listen to Pip, then say it back. Ready?',
  ],
  2: [
    'This time, you say it first. Pip will help if you need it.',
    'Your turn to name them. Pip is listening.',
    'See if you can name this one on your own.',
  ],
  3: [
    'Now tell Pip what to do. Two words.',
    'Ask Pip for what you want. Two words is all it takes.',
    'You are in charge now. Tell Pip in two words.',
  ],
};

/** Between rounds, so the pace never feels like a list. */
export const ROUND_LINKS = [
  'Here comes another.',
  'And the next one.',
  'Look up.',
  'Another one is floating over.',
  'Watch this one.',
  'Here we go.',
  'Something new.',
  'Oh, look at this one.',
];

/** End of a level. */
export const LEVEL_COMPLETE = [
  'That is the whole set. Wonderful.',
  'All of them popped. Look at that sky.',
  'Every bubble, done.',
  'You cleared them all.',
];

/** End of a session, or the end of the game’s content. */
export const SESSION_END = [
  'Thank you for playing with Pip.',
  'Pip had a lovely time. Come back soon.',
  'That was a good session. Well done.',
];

/**
 * Fill {placeholders}. Unknown placeholders are left in place rather than
 * silently blanked, so a missing variable is visible in testing instead of
 * producing a line that reads "Say ." to a child.
 *
 * @param {string} template
 * @param {Object<string,string>} vars
 * @returns {string}
 */
export function renderLine(template, vars = {}) {
  return String(template).replace(/\{(\w+)\}/g, (whole, key) =>
    (vars[key] === undefined || vars[key] === null ? whole : String(vars[key]))
  );
}

export default {
  PRAISE,
  ENCOURAGEMENT,
  NEARLY,
  ASSIST,
  PROMPTS,
  HINTS,
  LEVEL_INTRO,
  ROUND_LINKS,
  LEVEL_COMPLETE,
  SESSION_END,
  renderLine,
};
